import { useMutation, useQuery } from '@apollo/client';
import { Grid } from '@mui/material';
import Loader from 'Components/Forms/Loader';
import TextInputField from 'Components/Forms/TextInputField';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import useMessages from 'hooks/useMessages';
import { useLogEvent } from 'hooks/useLogEvent';
import Container from 'FeatureModules/Setting/Components/Container';
import KeysForm from 'FeatureModules/Setting/Components/Forms/KeysForm';
import WidgetPreferencesForm from 'FeatureModules/Setting/Components/WidgetPreferencesForm';
import ErrorState from 'Components/ErrorState';
import { EDIT_COMPANY, GENERATE_API_KEY, UPDATE_COMPANY_CONFIGS } from 'graphql/secure/mutations';
import { GET_INTEGRATION_SETTINGS } from 'graphql/secure/queries';
import { Form, Formik } from 'formik';
import React, { Fragment } from 'react';
import SettingsHeader from '../Components/SettingsHeader';

const IntegrationSettings = () => {
  const { setSuccessMessage, setErrorMessage } = useMessages();
  const { logEvent } = useLogEvent();

  const { data: { company } = {}, loading, error } = useQuery(GET_INTEGRATION_SETTINGS);

  const [generateKey, { loading: generatingKey }] = useMutation(GENERATE_API_KEY, {
    onCompleted: () => {
      setSuccessMessage('Success! API Key generated.');
    },
    onError: () => {
      setErrorMessage('Something went wrong generating your API Key.');
    },
    refetchQueries: ['IntegrationSettings'],
  });

  const [updateMapboxToken, { loading: savingMapboxToken }] = useMutation(EDIT_COMPANY, {
    onCompleted: () => setSuccessMessage('Mapbox API token saved.'),
    onError: () => setErrorMessage('Something went wrong saving the Mapbox API token.'),
  });

  const handleSubmitMapboxToken = ({ mapboxApiToken }) => {
    return updateMapboxToken({ variables: { input: { mapboxApiToken: mapboxApiToken || null } } });
  };

  const [updateCompanyConfig] = useMutation(UPDATE_COMPANY_CONFIGS, {
    onCompleted: () => {
      logEvent('CHANGE_INTEGRATION_SETTING');
      setSuccessMessage('Success! Widget preferences were updated.');
    },
    onError: () => {
      setErrorMessage('Something went wrong updating your widget preferences.');
    },
  });

  const handleSubmitWidgetPreferences = values => {
    return updateCompanyConfig({
      variables: {
        input: {
          integrationSettings: {
            ...values,
          },
        },
      },
    });
  };

  const handleSubmitKey = async () => {
    await generateKey();
  };

  if (error) return <ErrorState />;
  if (!company && loading) return <Loader />;

  return (
    <Fragment>
      <Container>
        <SettingsHeader title="API Key" />
        <KeysForm loading={generatingKey} onSubmit={handleSubmitKey} apiKey={company?.apiKey} />
      </Container>
      <Container>
        <SettingsHeader title="Mapbox API Token" />
        <Formik
          enableReinitialize
          initialValues={{ mapboxApiToken: company?.mapboxApiToken || '' }}
          onSubmit={handleSubmitMapboxToken}
        >
          {({ isSubmitting }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={8}>
                  <TextInputField
                    label="Mapbox API token"
                    name="mapboxApiToken"
                    placeholder="pk.eyJ1Ij..."
                    helperText='Your public Mapbox token (starts with "pk."). If provided, map usage will be attributed to your Mapbox account.'
                  />
                </Grid>
                <Grid item xs={12}>
                  <ThemeButton
                    size="large"
                    color="PRIMARY"
                    loading={isSubmitting || savingMapboxToken}
                    type="submit"
                  >
                    Save
                  </ThemeButton>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Container>
      {/* Without the api key the integration settings don't exist, so we show them only if they exist */}
      {company?.integrationSettings && (
        <Container>
          <SettingsHeader title="Integration Settings" />
          <WidgetPreferencesForm
            onSubmit={handleSubmitWidgetPreferences}
            integrationSettings={company?.integrationSettings}
          />
        </Container>
      )}
    </Fragment>
  );
};

export default IntegrationSettings;
